import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  onClear?: () => void;
}

/**
 * SearchInput Component v2 - FacturXPro Design System
 *
 * Input de recherche avec icône Search et effet glassmorphism
 *
 * @example
 * ```tsx
 * <SearchInput
 *   placeholder="Rechercher..."
 *   value={searchTerm}
 *   onChange={(e) => setSearchTerm(e.target.value)}
 * />
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Inputs - Search Input"
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  size = 'md',
  fullWidth = true,
  placeholder,
  value,
  onChange,
  onClear,
  className = '',
  ...props
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.actions.search');
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm pl-9',
    md: 'px-3 py-2 text-base pl-10',
    lg: 'px-4 py-3 text-lg pl-12',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4 left-2.5',
    md: 'w-5 h-5 left-3',
    lg: 'w-6 h-6 left-3.5',
  };

  const widthClasses = fullWidth ? 'w-full' : '';

  const showClear = value && onClear;

  return (
    <div className={`relative ${widthClasses}`}>
      <Search
        className={`absolute top-1/2 -translate-y-1/2 text-gray-400 ${iconSizeClasses[size]}`}
      />
      <input
        type="text"
        className={`
          ${sizeClasses[size]}
          ${widthClasses}
          bg-white/40
          border
          border-white/50
          rounded-2xl
          focus:ring-2
          focus:ring-teal-500
          focus:border-teal-500
          focus:bg-white
          hover:bg-white
          hover:border-teal-400
          hover:shadow-lg
          hover:shadow-teal-100/50
          transition-all
          duration-300
          outline-none
          placeholder:text-gray-400
          ${showClear ? 'pr-10' : ''}
          ${className}
        `.trim()}
        placeholder={resolvedPlaceholder}
        value={value}
        onChange={onChange}
        {...props}
      />
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default SearchInput;
